import { useEffect, useMemo, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp, X } from "lucide-react";
import {
  DsBadge,
  DsButton,
  DsEmptyState,
  DsPageHeader,
} from "../../../design-system";
import { usePermissions } from "../../../rbac/PermissionsContext";
import { useIsPhone } from "../../../hooks/useIsPhone";
import {
  WORKSPACE_MODULES,
  buildFeatureHref,
  getFeaturesForWorkspace,
  getWorkspaceDef,
  isFeatureGranted,
} from "../../../nav/workspaceRegistry";
import {
  employeeService,
  payrollService,
  salaryStructureService,
  branchService,
} from "../../../services/api";
import WorkspaceIoModal from "./WorkspaceIoModal";
import { WORKSPACE_IO_DEFS } from "./workspaceIoActions";
import ChecksImportModal from "./ChecksImportModal";
import SalaryRevisionImportModal from "../payroll/SalaryRevisionImportModal";
import StructureComponentImportModal from "../payroll/StructureComponentImportModal";
import MastersImportModal from "../masters/MastersImportModal";
import { templatesToStructures } from "../../../utils/payrollSalaryBreakdownEnrichment";
import { isCompanyModuleEnabled } from "../../../utils/companyModules";

/** Card hubs — section chosen from top-nav icon dropdown (not a page sidebar). */
const CARD_LAYOUT_WORKSPACES = new Set([
  "reports",
  "import_export",
  "checklist",
  "settings",
]);

const IO_MODAL_FEATURE_IDS = new Set(Object.keys(WORKSPACE_IO_DEFS));

function featureAllowed(feature, { isAdmin, hasScreenAccess }) {
  if (isAdmin) return true;
  return isFeatureGranted(feature, hasScreenAccess);
}

/**
 * Previous Import/Export hub card — module title, count, link list.
 * Sits in a 3-col grid slot so one selected module keeps the same card width.
 */
function ModuleHubCard({ module, onFeatureClick }) {
  const items = [...(module.cards || []), ...(module.links || [])];
  if (items.length === 0) {
    return (
      <DsEmptyState
        title="No options in this section"
        description="Ask an administrator for access, or pick another section from the top-bar dropdown."
      />
    );
  }
  return (
    <section className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
      <div className="flex items-center justify-between gap-2 border-b border-[hsl(var(--ds-border))] px-4 py-3">
        <h2 className="ds-h4 text-[hsl(var(--ds-fg))]">{module.label}</h2>
        <DsBadge tone="neutral">{items.length}</DsBadge>
      </div>
      <ul className="divide-y divide-[hsl(var(--ds-border))]">
        {items.map((feature) => (
          <li key={feature.id}>
            <button
              type="button"
              onClick={() => onFeatureClick(feature)}
              className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm font-medium text-[hsl(var(--ds-fg))] transition hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-primary))]"
            >
              <span className="min-w-0 truncate">{feature.label}</span>
              <ArrowRight size={14} className="shrink-0 opacity-50" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Phone-only section picker — desktop uses the top-nav icon dropdown. */
function PhoneSectionPicker({ modules, selectedKey, onSelect }) {
  if (!modules.length) return null;
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {modules.map((mod) => {
        const active = selectedKey === mod.key;
        return (
          <button
            key={mod.key}
            type="button"
            onClick={() => onSelect(mod.key)}
            className={`h-9 shrink-0 rounded-[11px] px-3 text-[13px] font-semibold transition-colors ${
              active
                ? "bg-[hsl(var(--ds-primary)/0.14)] text-[hsl(var(--ds-primary))]"
                : "border border-[hsl(var(--hrms-line))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--hrms-ink))]"
            }`}
          >
            {mod.label}
          </button>
        );
      })}
    </div>
  );
}

function SectionFeaturePanel({
  workspaceLabel,
  workspaceDescription,
  modules,
  selected,
  isPhone,
  onSelectSection,
  onFeatureClick,
  onBack,
  onClose,
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <DsPageHeader
          title={workspaceLabel}
          infoTooltip={workspaceDescription}
          className="!mb-0"
        />
        <div className="flex shrink-0 items-center gap-2">
          {selected ? (
            <DsButton
              type="button"
              variant="outline"
              size="sm"
              onClick={onBack}
              aria-label="Back to sections"
            >
              <ArrowLeft size={16} aria-hidden />
              Back
            </DsButton>
          ) : null}
          <DsButton
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label={`Close ${workspaceLabel}`}
          >
            <X size={16} aria-hidden />
            Close
          </DsButton>
        </div>
      </div>

      {isPhone ? (
        <PhoneSectionPicker
          modules={modules}
          selectedKey={selected?.key || ""}
          onSelect={onSelectSection}
        />
      ) : null}

      {selected ? (
        /* Same 3-col grid as before — only the chosen module card is mounted. */
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <ModuleHubCard module={selected} onFeatureClick={onFeatureClick} />
        </div>
      ) : (
        <DsEmptyState
          title="No section selected"
          description={
            isPhone
              ? "Tap a section chip above to see its options."
              : `Use the ${workspaceLabel} icon in the top bar and pick Employee, Attendance, Payroll, or another section.`
          }
        />
      )}
    </div>
  );
}

function AccordionModules({ modules, openModule, onToggle, onFeatureClick }) {
  return (
    <div className="space-y-3">
      {modules.map((mod) => {
        const open = openModule === mod.key;
        return (
          <section
            key={mod.key}
            className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]"
          >
            <button
              type="button"
              onClick={() => onToggle(mod.key)}
              className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition hover:bg-[hsl(var(--ds-muted))]/50"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="ds-h4 text-[hsl(var(--ds-fg))]">{mod.label}</h2>
                  <DsBadge tone="neutral">
                    {mod.cards.length + mod.links.length}
                  </DsBadge>
                </div>
                <p className="ds-caption mt-0.5 text-[hsl(var(--ds-muted-fg))]">
                  {open ? "Hide section" : "Show cards and quick links"}
                </p>
              </div>
              {open ? (
                <ChevronUp
                  size={18}
                  className="shrink-0 text-[hsl(var(--ds-muted-fg))]"
                />
              ) : (
                <ChevronDown
                  size={18}
                  className="shrink-0 text-[hsl(var(--ds-muted-fg))]"
                />
              )}
            </button>

            {open && (
              <div className="border-t border-[hsl(var(--ds-border))] px-4 py-4">
                <ModuleHubCard module={mod} onFeatureClick={onFeatureClick} />
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

export default function WorkspacePage({ workspaceKey: workspaceKeyProp }) {
  const { workspaceKey: workspaceKeyParam } = useParams();
  const location = useLocation();
  const workspaceKeyFromPath =
    (location.pathname.match(/\/admin\/workspace\/([^/?#]+)/) || [])[1] || "";
  const workspaceKey =
    workspaceKeyProp || workspaceKeyParam || workspaceKeyFromPath || "reports";
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const { isAdmin, hasScreenAccess } = usePermissions();

  const def = getWorkspaceDef(workspaceKey);
  const useCardLayout = CARD_LAYOUT_WORKSPACES.has(workspaceKey);

  const [ioFeatureId, setIoFeatureId] = useState(null);
  const [checksImportOpen, setChecksImportOpen] = useState(false);
  const [salaryRevisionOpen, setSalaryRevisionOpen] = useState(false);
  const [structureComponentsOpen, setStructureComponentsOpen] = useState(false);
  const [mastersImportOpen, setMastersImportOpen] = useState(false);
  const [salaryEmployees, setSalaryEmployees] = useState([]);
  const [allowMidMonth, setAllowMidMonth] = useState(false);
  const [structureEmployees, setStructureEmployees] = useState([]);
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [statutoryConfigJson, setStatutoryConfigJson] = useState(null);
  const [branchesList, setBranchesList] = useState([]);

  const features = useMemo(() => {
    return getFeaturesForWorkspace(workspaceKey).filter(
      (f) =>
        isCompanyModuleEnabled(f.moduleKey) &&
        featureAllowed(f, { isAdmin, hasScreenAccess })
    );
  }, [workspaceKey, isAdmin, hasScreenAccess]);

  const modulesWithFeatures = useMemo(() => {
    return WORKSPACE_MODULES.map((mod) => ({
      ...mod,
      cards: features.filter((f) => f.moduleKey === mod.key && f.kind === "card"),
      links: features.filter((f) => f.moduleKey === mod.key && f.kind === "link"),
    })).filter((mod) => mod.cards.length > 0 || mod.links.length > 0);
  }, [features]);

  const moduleFromUrl = searchParams.get("module") || "";
  const [openModule, setOpenModule] = useState(() => moduleFromUrl || "");

  useEffect(() => {
    if (!modulesWithFeatures.length) {
      setOpenModule("");
      return;
    }
    if (
      moduleFromUrl &&
      modulesWithFeatures.some((m) => m.key === moduleFromUrl)
    ) {
      setOpenModule(moduleFromUrl);
      return;
    }
    if (useCardLayout) {
      // Wait for top-nav dropdown (or phone chips) — do not auto-open a sidebar section.
      if (moduleFromUrl) setOpenModule("");
      return;
    }
    if (!modulesWithFeatures.some((m) => m.key === openModule)) {
      setOpenModule(modulesWithFeatures[0].key);
    }
  }, [modulesWithFeatures, moduleFromUrl, openModule, useCardLayout]);

  const selectModuleSection = (key) => {
    setOpenModule(key);
    const nextParams = new URLSearchParams(searchParams);
    if (key) nextParams.set("module", key);
    else nextParams.delete("module");
    setSearchParams(nextParams, { replace: true });
  };

  const clearSection = () => {
    setOpenModule("");
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("module");
    setSearchParams(nextParams, { replace: true });
  };

  const closeWorkspace = () => {
    navigate("/admin/home");
  };

  useEffect(() => {
    if (!salaryRevisionOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const [empRes, tplRes, statRes, branchesRes, settingsRes] =
          await Promise.all([
            employeeService.getEmployees({
              page: 1,
              limit: 5000,
              fields: "id,empCode,employeeCode,name,ctc,ctcAmount",
            }),
            salaryStructureService.listTemplates(),
            salaryStructureService.getStatutoryConfig(),
            branchService.getBranches(),
            payrollService.getPayrollSettings(),
          ]);
        if (cancelled) return;
        const list = empRes?.data || empRes?.employees || empRes || [];
        setSalaryEmployees(Array.isArray(list) ? list : []);
        setSalaryStructures(templatesToStructures(tplRes?.data) || []);
        setStatutoryConfigJson(statRes?.data?.configJson || null);
        const branches =
          branchesRes?.data || branchesRes?.branches || branchesRes || [];
        setBranchesList(Array.isArray(branches) ? branches : []);
        const settings = settingsRes?.data || settingsRes || {};
        setAllowMidMonth(!!settings.allowMidMonthIncrements);
      } catch {
        if (!cancelled) {
          setSalaryEmployees([]);
          setSalaryStructures([]);
          setStatutoryConfigJson(null);
          setBranchesList([]);
          setAllowMidMonth(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [salaryRevisionOpen]);

  useEffect(() => {
    if (!structureComponentsOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const [empRes, tplRes, statRes, branchesRes] = await Promise.all([
          employeeService.getEmployees({ page: 1, limit: 5000 }),
          salaryStructureService.listTemplates(),
          salaryStructureService.getStatutoryConfig(),
          branchService.getBranches(),
        ]);
        if (cancelled) return;
        const list = empRes?.data || empRes?.employees || empRes || [];
        setStructureEmployees(Array.isArray(list) ? list : []);
        setSalaryStructures(templatesToStructures(tplRes?.data) || []);
        setStatutoryConfigJson(statRes?.data?.configJson || null);
        const branches =
          branchesRes?.data || branchesRes?.branches || branchesRes || [];
        setBranchesList(Array.isArray(branches) ? branches : []);
      } catch {
        if (!cancelled) {
          setStructureEmployees([]);
          setSalaryStructures([]);
          setStatutoryConfigJson(null);
          setBranchesList([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [structureComponentsOpen]);

  const toggleModule = (key) => {
    const next = openModule === key ? "" : key;
    setOpenModule(next);
    const nextParams = new URLSearchParams(searchParams);
    if (next) nextParams.set("module", next);
    else nextParams.delete("module");
    setSearchParams(nextParams, { replace: true });
  };

  const openFeature = (feature) => {
    if (workspaceKey === "import_export") {
      if (feature.id === "pay-import-revision") {
        setSalaryRevisionOpen(true);
        return;
      }
      if (feature.id === "pay-import-structure-components") {
        setStructureComponentsOpen(true);
        return;
      }
      if (feature.id === "cfg-import-masters") {
        setMastersImportOpen(true);
        return;
      }
      if (feature.id === "att-import-checks") {
        setChecksImportOpen(true);
        return;
      }
      if (IO_MODAL_FEATURE_IDS.has(feature.id)) {
        setIoFeatureId(feature.id);
        return;
      }
    }
    navigate(buildFeatureHref(feature));
  };

  const selectedSection = useMemo(() => {
    if (!openModule) return null;
    return modulesWithFeatures.find((m) => m.key === openModule) || null;
  }, [modulesWithFeatures, openModule]);

  if (workspaceKey === "reports" && !isCompanyModuleEnabled("reports")) {
    return <Navigate to="/admin/home" replace />;
  }

  if (!def) {
    return (
      <div className="ds-page">
        <div className="ds-page-shell">
          <div className="ds-page-content">
            <DsEmptyState
              title="Workspace not found"
              description="This workspace hub is not configured."
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ds-page">
      <div className="ds-page-shell">
        <div className="ds-page-content space-y-4">
          {modulesWithFeatures.length === 0 ? (
            <DsEmptyState
              title={`No ${def.label.toLowerCase()} available`}
              description="Your role does not include any features in this workspace. Ask an administrator to grant access."
            />
          ) : useCardLayout ? (
            <SectionFeaturePanel
              workspaceLabel={def.label}
              workspaceDescription={def.description}
              modules={modulesWithFeatures}
              selected={selectedSection}
              isPhone={isPhone}
              onSelectSection={selectModuleSection}
              onFeatureClick={openFeature}
              onBack={clearSection}
              onClose={closeWorkspace}
            />
          ) : (
            <>
              <div className="flex items-start justify-between gap-3">
                <DsPageHeader title={def.label} infoTooltip={def.description} />
                <DsButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={closeWorkspace}
                  aria-label={`Close ${def.label}`}
                >
                  <X size={16} className="mr-1.5" aria-hidden />
                  Close
                </DsButton>
              </div>
              <AccordionModules
                modules={modulesWithFeatures}
                openModule={openModule}
                onToggle={toggleModule}
                onFeatureClick={openFeature}
              />
            </>
          )}
        </div>
      </div>

      <WorkspaceIoModal
        featureId={ioFeatureId}
        isOpen={!!ioFeatureId}
        onClose={() => setIoFeatureId(null)}
      />

      <ChecksImportModal
        isOpen={checksImportOpen}
        onClose={() => setChecksImportOpen(false)}
      />

      <SalaryRevisionImportModal
        isOpen={salaryRevisionOpen}
        onClose={() => setSalaryRevisionOpen(false)}
        employees={salaryEmployees}
        structures={salaryStructures}
        statutoryConfigJson={statutoryConfigJson}
        branchesList={branchesList}
        allowMidMonthIncrements={allowMidMonth}
        onImported={() => setSalaryRevisionOpen(false)}
      />

      <StructureComponentImportModal
        isOpen={structureComponentsOpen}
        onClose={() => setStructureComponentsOpen(false)}
        employees={structureEmployees}
        structures={salaryStructures}
        statutoryConfigJson={statutoryConfigJson}
        branchesList={branchesList}
        onImported={() => setStructureComponentsOpen(false)}
      />

      <MastersImportModal
        isOpen={mastersImportOpen}
        onClose={() => setMastersImportOpen(false)}
        onImported={() => setMastersImportOpen(false)}
      />
    </div>
  );
}
